import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password', { email });
      setSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password/confirm', { token, password, confirm_password: confirmPassword });
      toast.success('Password updated! Please login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-white flex items-center justify-center px-6 font-body">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-block mb-12">
          <span className="font-display text-2xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </span>
        </Link>

        {token ? (
          // Reset password form
          <>
            <h1 className="font-display text-4xl text-navy mb-2">Create New Password</h1>
            <p className="text-text-mid mb-8">Enter your new password below (min 8 characters, include uppercase, number, and special character)</p>

            <form onSubmit={handleResetPassword} className="space-y-6" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-navy font-medium">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    data-testid="reset-new-password-input"
                    className="h-12 rounded-xl border-seafoam focus:border-teal pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-light hover:text-teal"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-navy font-medium">Confirm Password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  data-testid="reset-confirm-password-input"
                  className="h-12 rounded-xl border-seafoam focus:border-teal"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-btn"
                className="w-full h-12 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </>
        ) : sent ? (
          // Confirmation message
          <div className="text-center">
            <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">✉️</span>
            </div>
            <h1 className="font-display text-4xl text-navy mb-4">Check Your Email</h1>
            <p className="text-text-mid mb-8">
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
            <Link 
              to="/login" 
              className="text-teal hover:text-teal-deep font-semibold"
            >
              Back to Login
            </Link>
          </div>
        ) : (
          // Request reset form
          <>
            <h1 className="font-display text-4xl text-navy mb-2">Reset Password</h1>
            <p className="text-text-mid mb-8">Enter your email and we'll send you reset instructions</p>

            <form onSubmit={handleRequestReset} className="space-y-6" data-testid="request-reset-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-navy font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="reset-email-input"
                  className="h-12 rounded-xl border-seafoam focus:border-teal"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="request-reset-btn"
                className="w-full h-12 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base"
              >
                {loading ? 'Sending...' : (
                  <>Send Reset Link <ArrowRight size={18} className="ml-2" /></>
                )}
              </Button>
            </form>

            <p className="text-center text-text-mid mt-8">
              Remember your password?{' '}
              <Link to="/login" className="text-teal hover:text-teal-deep font-semibold">
                Sign In
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
